import { useParams } from "next/navigation"
import { useMutation, type UseMutationOptions } from "@tanstack/react-query"

import type { AlertPresetKeys } from "~/lib/alert"
import { SHOPIFY_NEWSLETTER_PARAMS } from "~/lib/api/constants"
import { requestNextApiEndpoint } from "~/lib/api/utils/request-endpoint"
import { getI18nKey } from "~/lib/i18n/utils/get-i18n"
import { getProcessedLocale } from "~/lib/i18n/utils/get-processed-locale"
import { useProcessNewCustomerTags } from "~/hooks/account/useProcessNewCustomerTags"
import useIsE2E from "~/hooks/useIsE2E"
import type { NewsletterMutationProps } from "~/components/ui/Newsletter"

export type RecaptchaMutationProps<T> = T & {
  recaptcha?: {
    token: string
    action: string
  }
}

export default function useShopifyNewsletterMutation(
  mutationOptions?: UseMutationOptions<unknown, unknown, NewsletterMutationProps>
) {
  const isE2E = useIsE2E()

  const params = useParams()
  const lang = getI18nKey(getProcessedLocale(params?.locale), "lang")

  const customerTags = useProcessNewCustomerTags(params?.locale)

  return useMutation<unknown, Error, RecaptchaMutationProps<NewsletterMutationProps>>(
    async ({ email, acceptsMarketing = true, recaptcha }) => {
      return await requestNextApiEndpoint(SHOPIFY_NEWSLETTER_PARAMS, {
        ...(recaptcha && {
          headers: {
            "Recaptcha-Token": recaptcha.token,
            "Recaptcha-Action": recaptcha.action,
            "Recaptcha-Skip": isE2E ? "1" : "0",
          },
        }),
        payload: {
          email,
          lang,
          tags: customerTags,
          acceptsMarketing,
          skipRecaptcha: Boolean(isE2E),
        },
      })
    },
    {
      ...mutationOptions,
      onSuccess: (data, ...rest) => {
        mutationOptions?.onSuccess && mutationOptions.onSuccess(data, ...rest)
      },
      onError: (err, ...rest) => {
        const errMessage = JSON.parse(err?.message) as { success: boolean; data: AlertPresetKeys }
        mutationOptions?.onError?.(errMessage?.data, ...rest)
      },
    }
  )
}
